import axios, { AxiosResponse } from 'axios';
import moment from 'moment';
import { API_ENDPOINT } from '../../../env';
import { generateUuidV4 } from '../../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { getAuthHeaderForUser, handleAuthError } from '../../auth/auth.util';

interface TokensObject {
  [key: string]: {
    token: string;
    expiration: string;
  };
}

interface StoredEncryptedCredentials {
  encrypted_tokens_string: string;
  iv_key_string: string;
}

/**
 * Converts an ArrayBuffer to a Base64 string.
 * @param {ArrayBuffer} buffer The ArrayBuffer to convert.
 * @returns {string} The resulting Base64 encoded string.
 */
export const arrayBufferToBase64 = (buffer: Uint8Array<ArrayBuffer> | ArrayBuffer): string => {
  try {
    const buffer_byte_code_array: Uint8Array<ArrayBuffer> = new Uint8Array(buffer);
    let binary_string = '';
    for (let i = 0; i < buffer_byte_code_array.byteLength; i++) {
      binary_string += String.fromCharCode(buffer_byte_code_array[i]);
    }
    return btoa(binary_string);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: arrayBufferToBase64 failed on an unknown error.',
      { buffer: buffer },
      tracking_id
    );
  }
};

/**
 * Converts a Base64 string to an ArrayBuffer
 * @param {string} base_string The Base64 string to convert.
 * @returns {ArrayBuffer} The resulting ArrayBuffer.
 */
export const base64ToArrayBuffer = (base_string: string): ArrayBuffer => {
  try {
    const binary_string: string = atob(base_string);
    const byte_code_array: Uint8Array<ArrayBuffer> = new Uint8Array(binary_string.length);
    for (let i = 0; i < binary_string.length; i++) {
      byte_code_array[i] = binary_string.charCodeAt(i);
    }
    return byte_code_array.buffer;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: base64ToArrayBuffer failed on an unknown error.',
      { base_string: base_string },
      tracking_id
    );
  }
};

/**
 * Generates a device-specific encryption key.
 * @returns {Promise<CryptoKey>} The encryption key for the generating device.
 */
const getEncryptionKey = async (): Promise<CryptoKey> => {
  try {
    // Create a device fingerprint using various device specific variables
    const device_info: string = [
      navigator.userAgent,
      navigator.language,
      navigator.hardwareConcurrency,
      navigator.storage,
      navigator.maxTouchPoints
    ].join('•');

    // Add a static salt to make the key stronger
    // TODO: Update user creation to generate a salt for each user on creation to be used for various encryption.
    const salt_key = 'Duaeom8ESRmRL_)TzR=d';
    const final_material_key: string = device_info + salt_key;

    // Convert to an ArrayBuffer
    const encoder: TextEncoder = new TextEncoder();
    const crypto_key_data: Uint8Array<ArrayBufferLike> = encoder.encode(final_material_key);

    // Create a key from the device fingerprint using SHA-256
    const sha_base_key: ArrayBuffer = await crypto.subtle.digest(
      'SHA-256',
      crypto_key_data as ArrayBufferView<ArrayBuffer>
    );

    // Import the raw key material to get a CryptoKey object
    return crypto.subtle.importKey('raw', sha_base_key, { name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt'
    ]);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: getEncryptionKey failed on an unknown error.',
      {},
      tracking_id
    );
  }
};

/**
 * Encrypts an object containing cached report tokens and expiry dates.
 * @param {string} tokens_object The object of tokens and expiry times to encrypt.
 * @returns {Promise<StoredEncryptedCredentials>} Object containing the encrypted token and expiry times.
 */
const encryptPbiReportTokensObject = async (
  tokens_object: string
): Promise<StoredEncryptedCredentials> => {
  try {
    // Generate a random encryption key based on device/browser fingerprint + salt
    const encryption_key: CryptoKey = await getEncryptionKey();

    // Generate a random IV (Initalization Vector) for each encryption
    const iv_key: Uint8Array<ArrayBuffer> = crypto.getRandomValues(new Uint8Array(12));

    // Convert token to ArrayBuffer
    const encoder: TextEncoder = new TextEncoder();
    const token_data: Uint8Array<ArrayBufferLike> = encoder.encode(tokens_object);

    // Encrypt the token
    const encrypted_buffer: ArrayBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv_key
      },
      encryption_key,
      token_data as ArrayBufferView<ArrayBuffer>
    );

    // Convert encrypted data and IV to strings for storage
    const encrypted_tokens_string: string = arrayBufferToBase64(encrypted_buffer);
    const iv_key_string: string = arrayBufferToBase64(iv_key);

    return {
      encrypted_tokens_string,
      iv_key_string: iv_key_string
    };
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: encryptTokensObject failed on an unknown error.',
      { tokens_object: tokens_object },
      tracking_id
    );
  }
};

/**
 * Decrypts stored PowerBI Request Token Object.
 * @param {StoredEncryptedCredentials} encrypted_data Object containing encrypted tokens and expiry times.
 * @returns {Promise<string>} The decrypted token object.
 */
export const decryptPbiReportTokens = async (
  encrypted_data: StoredEncryptedCredentials
): Promise<TokensObject> => {
  try {
    const encryption_key: CryptoKey = await getEncryptionKey();
    // Convert base64 strings back to ArrayBuffers.
    const encrypted_token: ArrayBuffer = base64ToArrayBuffer(
      encrypted_data.encrypted_tokens_string
    );
    const iv_array_buffer: ArrayBuffer = base64ToArrayBuffer(encrypted_data.iv_key_string);
    // Decrypt the token object.
    const decrypted_buffer: ArrayBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv_array_buffer
      },
      encryption_key,
      encrypted_token
    );
    // Convert decrypted ArrayBuffer back to valid JSON object
    const decoder: TextDecoder = new TextDecoder();
    return JSON.parse(decoder.decode(decrypted_buffer));
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: decryptReportTokens failed on an unknown error.',
      { encrypted_data: encrypted_data },
      tracking_id
    );
  }
};

/**
 * Stores the encrypted token and its expiration in the localStorage encrypted token object.
 * @param {string} new_token The new auth token to add to token object.
 * @param {string} token_expiration_time  Timestamp string of token expiry.
 * @param {string} report_id  Report ID that token is linked to.
 * @returns {Promise<void>} The decrypted token object.
 */
export const storePbiReportTokens = async (
  new_token: string,
  token_expiration_time: string,
  report_id: string
): Promise<void> => {
  try {
    if (typeof localStorage !== 'undefined') {
      let encrypted_data: StoredEncryptedCredentials | null =
        await getStoredEncryptedPbiReportTokens();
      if (encrypted_data) {
        const decrypted_credentials: TokensObject = await decryptPbiReportTokens(encrypted_data);
        decrypted_credentials[report_id] = {
          token: new_token,
          expiration: moment(token_expiration_time).unix().toString()
        };
        encrypted_data = await encryptPbiReportTokensObject(JSON.stringify(decrypted_credentials));
      } else {
        const tokens_object: TokensObject = {};
        tokens_object[report_id] = {
          token: new_token,
          expiration: moment(token_expiration_time).unix().toString()
        };
        encrypted_data = await encryptPbiReportTokensObject(JSON.stringify(tokens_object));
      }

      localStorage.setItem('pbi_tokens', encrypted_data.encrypted_tokens_string);
      localStorage.setItem('pbi_iv_key', encrypted_data.iv_key_string);
    }
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: storePbiTokens failed on an unknown error.',
      { new_token: new_token, token_expiration_time: token_expiration_time, report_id: report_id },
      tracking_id
    );
  }
};

/**
 * Retrieves the stored encrypted token and IV key from localStorage.
 * @returns {StoredEncryptedCredentials|null} Object containing encrypted token and IV keys, or null if not found.
 */
const getStoredEncryptedPbiReportTokens = (): StoredEncryptedCredentials | null => {
  try {
    if (typeof localStorage !== 'undefined') {
      const encrypted_tokens: string | null = localStorage.getItem('pbi_tokens');
      const iv_key: string | null = localStorage.getItem('pbi_iv_key');
      if (encrypted_tokens && iv_key) {
        return {
          encrypted_tokens_string: encrypted_tokens,
          iv_key_string: iv_key
        };
      }
    }
    return null;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: getStoredEncryptedPbiTokens failed on an unknown error.',
      {},
      tracking_id
    );
  }
};

const MIN_EXPIRY_MS = 30 * 60000;

/**
 * Stores the encrypted token and its expiration in the stored encrypted token object.
 * @param {string} dataset_id Dataset of the report that the token will be generated for.
 * @param {string} report_id Report ID of report that the token will be generated for.
 * @returns {Promise<AxiosResponse>} The decrypted token object.
 */

const fetchNewPbiReportToken = async (
  dataset_id: string,
  report_id: string
): Promise<AxiosResponse> => {
  try {
    const powerbi_token: AxiosResponse = await axios({
      method: 'post',
      url: `${API_ENDPOINT}/get_bi_embed_token`,
      data: {
        datasets: [{ id: dataset_id }],
        reports: [
          {
            id: report_id
          }
        ]
      },
      headers: {
        ...(await getAuthHeaderForUser())
      }
    });
    return powerbi_token;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    handleAuthError(err);
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: fetchNewPbiToken failed on an unknown error.',
      {
        dataset_id: dataset_id,
        report_id: report_id
      },
      tracking_id
    );
  }
};

/**
 * Fetches and stores PBI token from storage, or generates a new one if not available or expired.
 *  @param {string} dataset_id ID of dataset belonging to report.
 *  @param {string} report_id ID of report being embedded.
 *  @param {boolean} force_refresh Optional force flag for a new token to be fetched instead of using cached.
 * @returns {Promise<string>} A valid authentication token.
 */
export const getPbiReportToken = async (
  dataset_id: string,
  report_id: string,
  force_refresh?: boolean
): Promise<string> => {
  try {
    // Check local storage for non expired PowerBI fetch key.
    const encrypted_data: StoredEncryptedCredentials | null = getStoredEncryptedPbiReportTokens();
    if (encrypted_data && !force_refresh) {
      const decrypted_data: TokensObject = await decryptPbiReportTokens(encrypted_data);
      if (decrypted_data) {
        if (decrypted_data[report_id]) {
          if (
            moment.unix(parseInt(decrypted_data[report_id].expiration)) >
            moment().add(MIN_EXPIRY_MS, 'ms')
          ) {
            return decrypted_data[report_id].token;
          }
        }
      }
    }
    // If no token found then generated a new one.
    const new_token: AxiosResponse = await fetchNewPbiReportToken(dataset_id, report_id);
    await storePbiReportTokens(new_token.data.token, new_token.data.expiration, report_id);
    return new_token.data.token;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: getPbiToken failed on an unknown error.',
      {
        dataset_id: dataset_id,
        report_id: report_id
      },
      tracking_id
    );
  }
};
